// src/pages/admin/UserLoginModulePageDetails.jsx
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";

import HeaderBar from "../components/HeaderBar";
import AdminLayoutWrapper from "../components/AdminLayoutWrapper";

import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { Printer, FileSpreadsheet, Search, ArrowLeft, ChevronLeft, ChevronRight } from "lucide-react";

import getCurrentSkin from "../utils/getCurrentSkin";
import { ThemeContext } from "../context/ThemeContext";

const BASE_URL = "https://nissi-india.com";

function getFinYearId() {
  try {
    const direct = localStorage.getItem("finYearId");
    if (direct) return String(direct);
    const admin = JSON.parse(localStorage.getItem("admin") || "null");
    if (admin?.finYearId) return String(admin.finYearId);
  } catch (_) {}
  return "";
}

function tenantHeaders() {
  const finYearId = getFinYearId();
  return finYearId ? { "x-fin-year-id": finYearId } : {};
}

function tenantParams(extra = {}) {
  const finYearId = getFinYearId();
  return finYearId ? { ...extra, finYearId } : extra;
}

function clamp(n, a, b) {
  return Math.max(a, Math.min(b, n));
}

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

// dd/MM/yyyy -> yyyy-MM-dd (for <input type="date"> UI if needed)
function dmyToISO(dmy) {
  if (!dmy) return "";
  const [dd, mm, yyyy] = String(dmy).split("/");
  if (!dd || !mm || !yyyy) return "";
  return `${yyyy}-${mm.padStart(2, "0")}-${dd.padStart(2, "0")}`;
}

// yyyy-MM-dd -> dd/MM/yyyy (for server)
function isoToDMY(iso) {
  if (!iso) return "";
  const [yyyy, mm, dd] = String(iso).split("-");
  if (!yyyy || !mm || !dd) return "";
  return `${dd}/${mm}/${yyyy}`;
}

export default function UserLoginModulePageDetails() {
  const navigate = useNavigate();
  const q = useQuery();

  const { skin, isDark } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const headerBgClass = `bg-${baseColor}-${baseShade}`;
  const headerTextClass = "text-white";
  const primaryBtnClass = `bg-${baseColor}-${baseShade}`;
  const primaryBtnHover = `hover:bg-${baseColor}-700`;

  const controlClass =
    "w-full rounded border border-gray-300 bg-white px-3 py-2 text-gray-900 placeholder-gray-400 disabled:bg-gray-100 disabled:text-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-300";

  const readOnlyClass =
    "w-full rounded border border-gray-300 bg-slate-50 px-3 py-2 text-gray-900";

  const lightBtnClass =
    "inline-flex items-center gap-2 rounded border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 hover:bg-slate-50 disabled:opacity-50";

  const pageBtnClass =
    "inline-flex items-center gap-1 rounded border border-gray-300 bg-white px-3 py-1 text-gray-900 disabled:opacity-50";

  const moduleId = Number(q.get("moduleId") || 0);
  const userName = q.get("userName") || "";
  const moduleName = q.get("moduleName") || "";

  const initialDateDMY = q.get("date") || "";
  const [dateISO, setDateISO] = useState(dmyToISO(initialDateDMY));
  const dateDMY = useMemo(() => isoToDMY(dateISO), [dateISO]);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const [total, setTotal] = useState(0);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / pageSize)), [total, pageSize]);

  const latestReq = useRef(0);
  const printFrameRef = useRef(null);

  const fetchList = async (opts = {}) => {
    const nextPage = opts.page ?? page;
    const nextPageSize = opts.pageSize ?? pageSize;

    if (!moduleId) {
      toast.error("moduleId missing in URL. Please go back and open again.");
      return;
    }

    setLoading(true);
    const reqId = Date.now();
    latestReq.current = reqId;

    try {
      const r = await axios.get(`${BASE_URL}/api/user-login-module-pages/list`, {
        headers: tenantHeaders(),
        params: tenantParams({
          moduleId,
          date: dateDMY || "",
          page: nextPage,
          pageSize: nextPageSize,
        }),
      });

      if (latestReq.current !== reqId) return;

      setRows(r.data?.rows || []);
      setTotal(Number(r.data?.total || 0));
      setPage(Number(r.data?.page || nextPage));
      setPageSize(Number(r.data?.pageSize || nextPageSize));
    } catch (e) {
      console.error(e);
      toast.error("Failed to load module page details");
    } finally {
      if (latestReq.current === reqId) setLoading(false);
    }
  };

  useEffect(() => {
    fetchList({ page: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSearch = () => {
    setPage(1);
    fetchList({ page: 1 });
  };

  const gotoPage = (p) => {
    const np = clamp(p, 1, totalPages);
    setPage(np);
    fetchList({ page: np });
  };

  const pageButtons = useMemo(() => {
    const windowSize = 5;
    const start = Math.max(1, page - Math.floor(windowSize / 2));
    const end = Math.min(totalPages, start + windowSize - 1);
    const s2 = Math.max(1, end - windowSize + 1);
    const arr = [];
    for (let i = s2; i <= end; i++) arr.push(i);
    return arr;
  }, [page, totalPages]);

  const onPrint = () => {
    if (!printFrameRef.current) {
      toast.error("Print frame not ready");
      return;
    }
    if (!moduleId) {
      toast.error("moduleId missing");
      return;
    }

    const qs = new URLSearchParams();
    qs.set("moduleId", String(moduleId));
    if (dateDMY) qs.set("date", dateDMY);
    qs.set("page", String(page));
    qs.set("pageSize", String(pageSize));
    if (userName) qs.set("userName", userName);
    if (moduleName) qs.set("moduleName", moduleName);
    const finYearId = getFinYearId();
    if (finYearId) qs.set("finYearId", finYearId);

    const url = `${BASE_URL}/api/user-login-module-pages/print?${qs.toString()}`;
    const iframe = printFrameRef.current;
    iframe.onload = () => {
      try {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
      } catch (err) {
        console.error(err);
        toast.error("Browser blocked printing. Try again.");
      }
    };
    iframe.src = url;
  };

  const onExcel = () => {
    if (!moduleId) {
      toast.error("moduleId missing");
      return;
    }
    const qs = new URLSearchParams();
    qs.set("moduleId", String(moduleId));
    if (dateDMY) qs.set("date", dateDMY);
    const finYearId = getFinYearId();
    if (finYearId) qs.set("finYearId", finYearId);

    window.location.href = `${BASE_URL}/api/user-login-module-pages/excel?${qs.toString()}`;
  };

  return (
    <AdminLayoutWrapper>
      <HeaderBar />
      <div className={`p-4 ${isDark ? "text-white" : "text-gray-900"}`}>
        <ToastContainer />

        <iframe ref={printFrameRef} title="print-frame" className="hidden" />

        <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className={lightBtnClass}
              title="Back"
            >
              <ArrowLeft className="h-4 w-4" />
              Back
            </button>

            <h1 className={`text-lg font-semibold ${isDark ? "text-white" : "text-gray-900"}`}>
              User Login Module Page Details
            </h1>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={onPrint}
              className={lightBtnClass}
              title="Print (current page only)"
              disabled={loading}
            >
              <Printer className="h-4 w-4" />
              Print
            </button>

            <button
              type="button"
              onClick={onExcel}
              className={lightBtnClass}
              title="Export Excel"
              disabled={loading}
            >
              <FileSpreadsheet className="h-4 w-4" />
              Excel
            </button>

            <select
              className="rounded border border-gray-300 bg-white px-2 py-2 text-sm text-gray-900"
              value={pageSize}
              onChange={(e) => {
                const ps = Number(e.target.value);
                setPageSize(ps);
                setPage(1);
                fetchList({ page: 1, pageSize: ps });
              }}
              disabled={loading}
              title="Rows per page"
            >
              {[10, 25, 50, 100, 200].map((n) => (
                <option key={n} value={n} className="bg-white text-gray-900">
                  {n}/page
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Filters */}
        <div className="rounded border bg-white p-4 text-gray-900">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-800">Date</label>
              <input
                type="date"
                className={controlClass}
                value={dateISO}
                onChange={(e) => setDateISO(e.target.value)}
                disabled
              />
              <div className="mt-1 text-xs text-slate-500">
                (Date is locked like ASPX. If you want editable, remove <b>disabled</b>.)
              </div>
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-gray-800">User Name</label>
              <input className={readOnlyClass} value={userName} readOnly />
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-gray-800">Module Name</label>
              <input className={readOnlyClass} value={moduleName} readOnly />
            </div>
          </div>

          <div className="mt-4">
            <button
              onClick={onSearch}
              disabled={loading}
              className={`inline-flex items-center gap-2 rounded px-4 py-2 text-white disabled:opacity-50 ${primaryBtnClass} ${primaryBtnHover}`}
            >
              <Search className="h-4 w-4" />
              {loading ? "Searching..." : "Search"}
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="mt-4 rounded border bg-white text-gray-900">
          <div className="max-h-[520px] overflow-auto">
            <table className="min-w-[800px] w-full border-collapse">
              <thead className="sticky top-0 z-10">
                <tr className={`${headerBgClass} ${headerTextClass} text-sm`}>
                  <th className="border border-white/20 px-2 py-2 text-center">Slno</th>
                  <th className="border border-white/20 px-2 py-2">Date</th>
                  <th className="border border-white/20 px-2 py-2">InTime</th>
                  <th className="border border-white/20 px-2 py-2">PageName</th>
                </tr>
              </thead>

              <tbody className="text-sm text-gray-900">
                {loading ? (
                  [...Array(6)].map((_, i) => (
                    <tr key={i}>
                      <td className="border px-2 py-3" colSpan={4}>
                        <div className="h-4 w-full animate-pulse rounded bg-slate-100" />
                      </td>
                    </tr>
                  ))
                ) : rows.length === 0 ? (
                  <tr>
                    <td className="px-3 py-6 text-center text-slate-500" colSpan={4}>
                      No Records Found
                    </td>
                  </tr>
                ) : (
                  rows.map((r, idx) => (
                    <tr key={`${r.fld_pageid}-${idx}`} className="text-gray-900 hover:bg-slate-50">
                      <td className="border px-2 py-2 text-center">
                        {(page - 1) * pageSize + (idx + 1)}
                      </td>
                      <td className="border px-2 py-2">{r.fld_date}</td>
                      <td className="border px-2 py-2">{r.fld_intime}</td>
                      <td className="border px-2 py-2">{r.fld_pagename}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-2 p-3 text-sm text-gray-800">
            <div className="text-slate-600">
              Total: <span className="font-semibold">{total}</span>
              {total > 0 && (
                <>
                  {" "}
                  | Showing{" "}
                  <span className="font-semibold">{(page - 1) * pageSize + 1}</span> -{" "}
                  <span className="font-semibold">{Math.min(page * pageSize, total)}</span>
                </>
              )}
            </div>

            <div className="flex items-center gap-2">
              <button
                className={pageBtnClass}
                disabled={loading || page <= 1}
                onClick={() => gotoPage(page - 1)}
              >
                <ChevronLeft className="h-4 w-4" />
                Prev
              </button>

              {pageButtons[0] > 1 && (
                <>
                  <button className={pageBtnClass} onClick={() => gotoPage(1)} disabled={loading}>
                    1
                  </button>
                  <span className="px-1 text-slate-500">…</span>
                </>
              )}

              {pageButtons.map((p) => (
                <button
                  key={p}
                  className={`rounded border border-gray-300 px-3 py-1 ${
                    p === page ? "bg-slate-900 text-white" : "bg-white text-gray-900"
                  }`}
                  disabled={loading}
                  onClick={() => gotoPage(p)}
                >
                  {p}
                </button>
              ))}

              {pageButtons[pageButtons.length - 1] < totalPages && (
                <>
                  <span className="px-1 text-slate-500">…</span>
                  <button
                    className={pageBtnClass}
                    onClick={() => gotoPage(totalPages)}
                    disabled={loading}
                  >
                    {totalPages}
                  </button>
                </>
              )}

              <button
                className={pageBtnClass}
                disabled={loading || page >= totalPages}
                onClick={() => gotoPage(page + 1)}
              >
                Next
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>

        {!moduleId && (
          <div className="mt-2 rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            moduleId missing. Open this page from “View” button so query params are passed.
          </div>
        )}
      </div>
    </AdminLayoutWrapper>
  );
}